import React, { useState, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  IconButton,
  Chip,
  Tooltip,
  Paper,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  TodayOutlined as TodayIcon,
  ScheduleOutlined as ScheduleIcon
} from '@mui/icons-material';

const CalendarView = ({ scheduledTasks, properties, onTaskClick }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month'); // 'month' or 'week'

  const getPropertyName = (propertyId) => {
    const property = properties.find(p => p.id === propertyId);
    return property ? property.name : 'Unknown Property';
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  // Generate calendar days based on view mode
  const calendarDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const day = currentDate.getDate();
    
    if (viewMode === 'week') {
      // Week view: show only the current week (7 days)
      const startOfWeek = new Date(currentDate);
      startOfWeek.setDate(day - currentDate.getDay()); // Start from Sunday
      
      const days = [];
      for (let i = 0; i < 7; i++) {
        const date = new Date(startOfWeek);
        date.setDate(startOfWeek.getDate() + i);
        days.push(date);
      }
      return days;
    } else {
      // Month view: show full month with previous/next month days
      const firstDay = new Date(year, month, 1);
      const startDate = new Date(firstDay);
      startDate.setDate(startDate.getDate() - firstDay.getDay());
      
      const days = [];
      const current = new Date(startDate);
      
      // Generate 42 days (6 weeks)
      for (let i = 0; i < 42; i++) {
        days.push(new Date(current));
        current.setDate(current.getDate() + 1);
      }
      
      return days;
    }
  }, [currentDate, viewMode]);

  // Get tasks for a specific date (compare calendar-day strings, not UTC ISO)
  const getTasksForDate = (date) => {
    const pad = (n) => String(n).padStart(2, "0");
    const dateStr = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
    return scheduledTasks.filter((task) => {
      if (!task.isActive || !task.nextDueDate) return false;
      const raw = task.nextDueDate;
      const taskDate =
        typeof raw === "string" && /^\d{4}-\d{2}-\d{2}/.test(raw)
          ? raw.slice(0, 10)
          : (() => {
              const d = raw?.toDate ? raw.toDate() : new Date(raw);
              return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
            })();
      return taskDate === dateStr;
    });
  };

  // Navigate to previous period
  const handlePreviousPeriod = () => {
    if (viewMode === 'week') {
      setCurrentDate(prev => {
        const newDate = new Date(prev);
        newDate.setDate(prev.getDate() - 7);
        return newDate;
      });
    } else {
      setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
    }
  };

  // Navigate to next period
  const handleNextPeriod = () => {
    if (viewMode === 'week') {
      setCurrentDate(prev => {
        const newDate = new Date(prev);
        newDate.setDate(prev.getDate() + 7);
        return newDate;
      });
    } else {
      setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
    }
  };

  // Go to today
  const handleToday = () => {
    setCurrentDate(new Date());
  };

  // Check if date is today
  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  // Check if date is in current month
  const isCurrentMonth = (date) => {
    return date.getMonth() === currentDate.getMonth();
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      {/* Calendar Header */}
      <Card sx={{ mb: { xs: 2, sm: 3 } }}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Box 
            display="flex" 
            flexDirection={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between" 
            alignItems={{ xs: 'flex-start', sm: 'center' }} 
            mb={{ xs: 1.5, sm: 2 }}
            gap={{ xs: 1.5, sm: 0 }}
          >
            <Box display="flex" alignItems="center" gap={{ xs: 1, sm: 2 }} flexWrap="wrap">
              <Typography 
                variant="h5"
                sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
              >
                {viewMode === 'week' ? (
                  (() => {
                    const startOfWeek = new Date(currentDate);
                    startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());
                    const endOfWeek = new Date(startOfWeek);
                    endOfWeek.setDate(startOfWeek.getDate() + 6);
                    
                    if (startOfWeek.getMonth() === endOfWeek.getMonth()) {
                      return isMobile 
                        ? `${startOfWeek.getDate()}-${endOfWeek.getDate()} ${monthNames[startOfWeek.getMonth()].substring(0, 3)}`
                        : `${startOfWeek.getDate()} - ${endOfWeek.getDate()} ${monthNames[startOfWeek.getMonth()]} ${startOfWeek.getFullYear()}`;
                    } else {
                      return isMobile
                        ? `${startOfWeek.getDate()} ${monthNames[startOfWeek.getMonth()].substring(0, 3)} - ${endOfWeek.getDate()} ${monthNames[endOfWeek.getMonth()].substring(0, 3)}`
                        : `${startOfWeek.getDate()} ${monthNames[startOfWeek.getMonth()]} - ${endOfWeek.getDate()} ${monthNames[endOfWeek.getMonth()]} ${startOfWeek.getFullYear()}`;
                    }
                  })()
                ) : (
                  isMobile
                    ? `${monthNames[currentDate.getMonth()].substring(0, 3)} ${currentDate.getFullYear()}`
                    : `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`
                )}
              </Typography>
              <FormControl size="small" sx={{ minWidth: { xs: 100, sm: 120 } }}>
                <InputLabel>View</InputLabel>
                <Select
                  value={viewMode}
                  onChange={(e) => setViewMode(e.target.value)}
                  label="View"
                >
                  <MenuItem value="month">Month</MenuItem>
                  <MenuItem value="week">Week</MenuItem>
                </Select>
              </FormControl>
            </Box>
            
            <Box display="flex" alignItems="center" gap={{ xs: 0.5, sm: 1 }}>
              <IconButton onClick={handlePreviousPeriod} size={isMobile ? 'small' : 'medium'} aria-label="Previous period">
                <ChevronLeftIcon fontSize={isMobile ? 'small' : 'medium'} />
              </IconButton>
              <Button
                variant="outlined"
                startIcon={<TodayIcon />}
                onClick={handleToday}
                size={isMobile ? 'small' : 'small'}
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
              >
                {isMobile ? 'Today' : 'Today'}
              </Button>
              <IconButton onClick={handleNextPeriod} size={isMobile ? 'small' : 'medium'} aria-label="Next period">
                <ChevronRightIcon fontSize={isMobile ? 'small' : 'medium'} />
              </IconButton>
            </Box>
          </Box>

          {/* Calendar Grid */}
          <Paper elevation={1}>
            <Box sx={{ overflowX: 'auto', overflowY: 'hidden' }}>
              <Box sx={{ minWidth: { xs: 600, sm: 700 } }}>
                {/* Day Headers */}
                <Box sx={{ display: 'flex', borderBottom: '1px solid', borderColor: 'divider' }}>
                  {dayNames.map(day => (
                    <Box 
                      key={day} 
                      sx={{ 
                        flex: 1,
                        p: { xs: 0.75, sm: 1 }, 
                        textAlign: 'center', 
                        fontWeight: 'bold',
                        borderRight: '1px solid',
                        borderColor: 'divider',
                        '&:last-child': { borderRight: 'none' },
                        minWidth: { xs: 85, sm: 'auto' }
                      }}
                    >
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                      >
                        {isMobile ? day.substring(0, 1) : day}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {/* Calendar Days */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                  {calendarDays.map((date, index) => {
                    const tasks = getTasksForDate(date);
                    const isCurrentMonthDay = isCurrentMonth(date);
                    const isTodayDate = isToday(date);
                    
                    return (
                      <Box 
                        key={index}
                        sx={{ 
                          width: { xs: 85, sm: '14.2857%' }, // Fixed width on mobile, percentage on larger screens
                          minWidth: { xs: 85, sm: 'auto' },
                          minHeight: viewMode === 'week' 
                            ? { xs: 120, sm: 180, md: 200 } 
                            : { xs: 80, sm: 120, md: 140 },
                          border: '1px solid',
                          borderColor: 'divider',
                          p: { xs: 0.5, sm: 1 },
                          position: 'relative',
                          backgroundColor: isTodayDate ? 'action.hover' : 'transparent',
                          borderTop: index < 7 ? 'none' : '1px solid',
                          borderLeft: index % 7 === 0 ? 'none' : '1px solid',
                          '&:nth-of-type(7n)': { borderRight: 'none' },
                          boxSizing: 'border-box'
                        }}
                      >
                        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: isTodayDate ? 'bold' : 'normal',
                              color: isCurrentMonthDay ? 'text.primary' : 'text.disabled',
                              fontSize: { xs: '0.75rem', sm: '0.875rem' }
                            }}
                          >
                            {date.getDate()}
                          </Typography>
                          
                          {tasks.length > 0 && (
                            <Chip
                              label={tasks.length}
                              size="small"
                              color="primary"
                              sx={{ 
                                minWidth: { xs: 18, sm: 20 }, 
                                height: { xs: 18, sm: 20 }, 
                                fontSize: { xs: '0.65rem', sm: '0.7rem' } 
                              }}
                            />
                          )}
                        </Box>

                        {/* Tasks for this date */}
                        <Box sx={{ mt: { xs: 0.5, sm: 1 } }}>
                          {tasks.slice(0, isMobile ? 2 : 3).map((task, taskIndex) => (
                            <Tooltip
                              key={taskIndex}
                              title={`${task.title} - ${getPropertyName(task.propertyId)}`}
                              arrow
                            >
                              <Chip
                                label={isMobile ? task.title.substring(0, 10) + '...' : task.title}
                                size="small"
                                color={getPriorityColor(task.priority)}
                                onClick={() => onTaskClick(task)}
                                sx={{ 
                                  mb: { xs: 0.25, sm: 0.5 }, 
                                  fontSize: { xs: '0.65rem', sm: '0.7rem' },
                                  height: { xs: 18, sm: 20 },
                                  cursor: 'pointer',
                                  maxWidth: '100%',
                                  '&:hover': {
                                    opacity: 0.8
                                  }
                                }}
                              />
                            </Tooltip>
                          ))}
                          
                          {tasks.length > (isMobile ? 2 : 3) && (
                            <Typography 
                              variant="caption" 
                              color="text.secondary"
                              sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                            >
                              +{tasks.length - (isMobile ? 2 : 3)} more
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Box>
          </Paper>
        </CardContent>
      </Card>

      {/* Legend */}
      <Card>
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Typography 
            variant="h6" 
            gutterBottom
            sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' }, mb: { xs: 1.5, sm: 2 } }}
          >
            Legend
          </Typography>
          <Box display="flex" gap={{ xs: 1.5, sm: 2 }} flexWrap="wrap">
            <Box display="flex" alignItems="center" gap={{ xs: 0.75, sm: 1 }}>
              <Chip 
                label={isMobile ? "High" : "High Priority"} 
                color="error" 
                size="small"
                sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
              />
              <Typography 
                variant="body2"
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
              >
                {isMobile ? "High" : "High Priority Tasks"}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={{ xs: 0.75, sm: 1 }}>
              <Chip 
                label={isMobile ? "Medium" : "Medium Priority"} 
                color="warning" 
                size="small"
                sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
              />
              <Typography 
                variant="body2"
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
              >
                {isMobile ? "Medium" : "Medium Priority Tasks"}
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={{ xs: 0.75, sm: 1 }}>
              <Chip 
                label={isMobile ? "Low" : "Low Priority"} 
                color="success" 
                size="small"
                sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
              />
              <Typography 
                variant="body2"
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
              >
                {isMobile ? "Low" : "Low Priority Tasks"}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CalendarView;